import * as React from 'react';
import {Query} from 'react-apollo';

import {Dialog} from 'shells/dialog/dialog';
import {Modal} from 'shells/modal/modal';

import type {
    GetEmailSequenceTemplate as GetEmailSequenceTemplateQuery,
    GetEmailSequenceTemplateVariables as GetEmailSequenceTemplateQueryVariables,
    GetQueuedEmail as GetQueuedEmailQuery,
    GetQueuedEmailVariables as GetQueuedEmailQueryVariables,
    GetEmailSequenceTemplate_emailSequenceTemplate,
    EmailSequenceTemplateCreateInput,
} from 'nutshell-graphql-types';

import GET_EMAIL_SEQUENCE_TEMPLATE from '../graphql/queries/get-email-sequence-template.graphql';
import GET_QUEUED_EMAIL from '../mailbox/graphql/queries/get-queued-email.graphql';

import {copySharedSequenceTemplate} from './copy-shared-sequence-template';
import {createSequenceTemplate} from './create-empty-sequence-template';
import {EmailTemplateDesignerCreateForm} from './email-template-designer-create-form';
import {EmailTemplateDesignerForm} from './email-template-designer-form';

type OwnProps = {
    isOpen: boolean;
    onClose: () => void;
    onChildTemplateSaved: () => void;
    sequenceTemplateId: string | null | undefined;
    sentEmailId: string | null | undefined;
    groupId?: string | null | undefined;
    refetchGroups?: () => Promise<any>;
    createEmailSequenceTemplate?: (input: EmailSequenceTemplateCreateInput) => Promise<any>;
};

export type EmailSequenceTemplate = GetEmailSequenceTemplate_emailSequenceTemplate;

type Props = OwnProps & {
    isLoading: boolean;
    emailSequenceTemplate: EmailSequenceTemplate | null | undefined;
    onRefetch?: () => Promise<any>;
};

type State = {
    emailSequenceTemplate: EmailSequenceTemplate | null | undefined;
    showProInfo: boolean;
    isShowingCloseDialog: boolean;
    childTemplateDirty: boolean;
};

class EmailTemplateDesignerModalComponent extends React.Component<Props, State> {
    closeButtonRef: HTMLElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();

        this.state = {
            emailSequenceTemplate: null,
            showProInfo: false,
            isShowingCloseDialog: false,
            childTemplateDirty: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (
            nextProps.emailSequenceTemplate &&
            (!this.state.emailSequenceTemplate ||
                nextProps.emailSequenceTemplate.id !== this.state.emailSequenceTemplate.id)
        ) {
            this.setState({
                emailSequenceTemplate: nextProps.emailSequenceTemplate,
            });
        }
    }

    render() {
        return (
            <>
                <Modal
                    headerText={this.getModalHeaderText()}
                    headerSubtext={this.getModalHeaderSubtext()}
                    isWide={!this.isChoosingStarterTemplate()}
                    isOpen={this.props.isOpen}
                    canClickOutsideToClose={false}
                    canEscapeToClose={false}
                    onBeforeClose={this.handleCloseButtonPress}
                    maxHeight={this.isChoosingStarterTemplate() ? 560 : undefined}
                    // It should mount along with the app, but better to be safe,
                    // since quill will blow up if we render without mounting
                    waitForMount={true}
                    getCloseButtonRef={(ref) => {
                        this.closeButtonRef = ref;
                    }}
                >
                    {this.getContentComponent()}
                </Modal>
                {this.state.isShowingCloseDialog ? (
                    <Dialog
                        anchor={this.closeButtonRef}
                        question='Uh oh! You’re about to lose your unsaved changes. Are you sure you’d like to close this and cancel your changes?'
                        primaryText='Yes, cancel changes'
                        secondaryText='No, keep editing'
                        isWarning={true}
                        onPrimary={this.handleClose}
                        onSecondary={() => {
                            this.setState({isShowingCloseDialog: false});
                        }}
                        location='bottom'
                    />
                ) : undefined}
            </>
        );
    }

    getContentComponent() {
        if (this.state.emailSequenceTemplate || this.props.isLoading) {
            return (
                <EmailTemplateDesignerForm
                    isLoading={this.props.isLoading}
                    closeModal={this.handleClose}
                    onChildTemplateSaved={this.props.onChildTemplateSaved}
                    emailSequenceTemplate={this.state.emailSequenceTemplate}
                    onSetShowProInfo={this.setShowProInfo}
                    startEditingSharedTemplate={this.handleEditSharedTemplate}
                    shouldShowProInfo={this.state.showProInfo}
                    onUserChildTemplateDirtied={() => {
                        this.setState({childTemplateDirty: true});
                    }}
                    onRefetch={this.props.onRefetch}
                    groupId={this.props.groupId}
                    refetchGroups={this.props.refetchGroups}
                    createEmailSequenceTemplate={this.props.createEmailSequenceTemplate}
                />
            );
        }

        if (this.isChoosingStarterTemplate()) {
            return (
                <EmailTemplateDesignerCreateForm
                    onCreateNew={(emailSequenceTemplate: EmailSequenceTemplate) => {
                        this.setState({
                            emailSequenceTemplate: emailSequenceTemplate,
                        });
                    }}
                />
            );
        }
    }

    getModalHeaderText(): string {
        if (this.state.showProInfo) {
            return '';
        }

        const title = this.isChoosingStarterTemplate() ? 'Create an email template' : 'Loading…';

        return this.state.emailSequenceTemplate ? this.state.emailSequenceTemplate.name : title;
    }

    getModalHeaderSubtext = () => {
        const emailSequenceTemplate = this.state.emailSequenceTemplate;
        if (!emailSequenceTemplate) return null;

        const userName = emailSequenceTemplate.parent
            ? emailSequenceTemplate.parent.creatorUser.name
            : emailSequenceTemplate.creatorUser.name;

        if (!userName || userName.length === 0) {
            return;
        }

        return `Created by ${userName}`;
    };

    setShowProInfo = (proShown: boolean) => {
        this.setState({showProInfo: proShown});
    };

    isChoosingStarterTemplate = () => {
        return (
            !this.props.isLoading &&
            !this.props.sequenceTemplateId &&
            !this.state.emailSequenceTemplate
        );
    };

    handleEditSharedTemplate = () => {
        this.setState((prevState) => ({
            emailSequenceTemplate:
                // emailSequenceTemplate should already be set, but have to make flow happy
                prevState.emailSequenceTemplate &&
                copySharedSequenceTemplate(prevState.emailSequenceTemplate),
        }));
    };

    handleCloseButtonPress = () => {
        if (!this.state.childTemplateDirty) {
            this.handleClose();

            return;
        }

        this.setState({isShowingCloseDialog: true});

        return false;
    };

    handleClose = () => {
        this.setState({
            emailSequenceTemplate: null,
            isShowingCloseDialog: false,
            childTemplateDirty: false,
        });
        this.props.onClose();
    };
}

class SequenceTemplateQuery extends Query<
    GetEmailSequenceTemplateQuery,
    GetEmailSequenceTemplateQueryVariables
> {}

class QueuedEmailQuery extends Query<GetQueuedEmailQuery, GetQueuedEmailQueryVariables> {}

export const EmailTemplateDesignerModal = (props: OwnProps) => (
    <SequenceTemplateQuery
        query={GET_EMAIL_SEQUENCE_TEMPLATE}
        skip={!props.sequenceTemplateId}
        variables={{id: props.sequenceTemplateId || '' /* skip prop makes this safe */}}
        // @ts-expect-error
        fetchPolicy='cache-and-network'
    >
        {(sequenceTemplateResults) => {
            return (
                <QueuedEmailQuery
                    query={GET_QUEUED_EMAIL}
                    skip={!props.sentEmailId}
                    variables={{
                        queuedEmailId: props.sentEmailId || '' /* skip prop makes this safe */,
                    }}
                    // @ts-expect-error
                    fetchPolicy='cache-and-network'
                >
                    {(queuedEmailResults) => {
                        let emailSequenceTemplate =
                            !sequenceTemplateResults.loading && sequenceTemplateResults.data
                                ? sequenceTemplateResults.data.emailSequenceTemplate
                                : null;

                        if (
                            !emailSequenceTemplate &&
                            !queuedEmailResults.loading &&
                            queuedEmailResults.data
                        ) {
                            emailSequenceTemplate = createSequenceTemplate(
                                queuedEmailResults.data.queuedEmail
                            );
                        }

                        const sequenceTemplateId = props.sequenceTemplateId;

                        return (
                            <EmailTemplateDesignerModalComponent
                                {...props}
                                isLoading={
                                    sequenceTemplateResults.loading || queuedEmailResults.loading
                                }
                                emailSequenceTemplate={emailSequenceTemplate}
                                onRefetch={
                                    sequenceTemplateId
                                        ? () => {
                                              return sequenceTemplateResults.refetch({
                                                  id: sequenceTemplateId,
                                              });
                                          }
                                        : undefined
                                }
                            />
                        );
                    }}
                </QueuedEmailQuery>
            );
        }}
    </SequenceTemplateQuery>
);
